import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { toIsoDate, type PeriodRow, type PeriodSuggestion } from '@/core/models/practice.models';
import { notifyError } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { dateRangeProblem } from '@/core/utils/dates';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DateField, type DateValue } from '@/kit/DateField';
import { Icon } from '@/kit/Icon';
import { isUnincorporated, useClientKind } from '@/features/tr-reportsettings/clientKind';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { calculateStatutoryDueDates } from './statutoryDates';

export interface AccountingPeriodDialogData {
  clientId: string;
  period?: PeriodRow;
  suggestion?: PeriodSuggestion;
  isPrior?: boolean;
  isFirstPeriod?: boolean;
}

interface PeriodFormValue {
  periodFrom: DateValue;
  periodTo: DateValue;
  accountsDueDate: DateValue;
  ct600DueDate: DateValue;
  lockPreviousPeriod: boolean;
  isCompanyCeased: boolean;
  forComparative: boolean;
}

function initialForm(data: AccountingPeriodDialogData | null): PeriodFormValue {
  const form: PeriodFormValue = {
    periodFrom: null,
    periodTo: null,
    accountsDueDate: null,
    ct600DueDate: null,
    lockPreviousPeriod: false,
    isCompanyCeased: false,
    forComparative: false,
  };
  const period = data?.period;
  const suggestion = data?.suggestion;
  if (period) {
    form.periodFrom = period.periodFrom;
    form.periodTo = period.periodTo;
    form.accountsDueDate = period.accountsDue;
    form.ct600DueDate = period.ct600Due;
    form.isCompanyCeased = period.isCeased;
    form.forComparative = period.isComparative;
  } else if (suggestion) {
    form.periodFrom = suggestion.periodFrom;
    form.periodTo = suggestion.periodTo;
    form.accountsDueDate = suggestion.accountsDueDate;
    form.ct600DueDate = suggestion.ct600DueDate;
    form.forComparative = !!data?.isPrior;
  }
  return form;
}

const isBlank = (value: DateValue) => value === null || value === undefined || value === '';

/** Angular AddAccountingPeriodComponent: add or edit one accounting period. Closes with true after a server save. */
export function AddAccountingPeriod({ data, dialogRef }: DialogComponentProps<AccountingPeriodDialogData, unknown>) {
  const [form, setForm] = useState<PeriodFormValue>(() => initialForm(data));
  const clientKind = useClientKind(data?.clientId);
  // BUG-078: a sole trader or partnership has no Companies House / CT600 deadline to enter.
  const noDueDates = isUnincorporated(clientKind?.clientType);
  const [isSaving, setIsSaving] = useState(false);
  // FormValidationService marked every control touched on a failed Save: that is what draws the red outlines.
  const [touched, setTouched] = useState(false);

  const set = <K extends keyof PeriodFormValue>(key: K, value: PeriodFormValue[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const onPeriodFromChange = (newFrom: Dayjs | null) => {
    setForm((current) => {
      const updated: PeriodFormValue = { ...current, periodFrom: newFrom };
      let targetTo = current.periodTo;
      if (newFrom && newFrom.isValid()) {
        if (!data?.period) {
          targetTo = newFrom.add(1, 'year').subtract(1, 'day');
          updated.periodTo = targetTo;
        }
      }
      if (!noDueDates && targetTo) {
        const { accountsDueDate, ct600DueDate } = calculateStatutoryDueDates({
          periodFrom: newFrom,
          periodTo: targetTo,
          clientType: clientKind?.clientType,
          companyType: clientKind?.companyType,
          businessStartDate: clientKind?.businessStartDate,
          isFirstPeriod: data?.isFirstPeriod,
        });
        if (accountsDueDate) updated.accountsDueDate = accountsDueDate;
        if (ct600DueDate) updated.ct600DueDate = ct600DueDate;
      }
      return updated;
    });
  };

  const onPeriodToChange = (newTo: Dayjs | null) => {
    setForm((current) => {
      const updated: PeriodFormValue = { ...current, periodTo: newTo };
      if (!noDueDates && newTo) {
        const { accountsDueDate, ct600DueDate } = calculateStatutoryDueDates({
          periodFrom: current.periodFrom,
          periodTo: newTo,
          clientType: clientKind?.clientType,
          companyType: clientKind?.companyType,
          businessStartDate: clientKind?.businessStartDate,
          isFirstPeriod: data?.isFirstPeriod,
        });
        if (accountsDueDate) updated.accountsDueDate = accountsDueDate;
        if (ct600DueDate) updated.ct600DueDate = ct600DueDate;
      }
      return updated;
    });
  };

  const onSave = () => {
    const required = noDueDates
      ? [form.periodFrom, form.periodTo]
      : [form.periodFrom, form.periodTo, form.accountsDueDate, form.ct600DueDate];
    if (required.some(isBlank)) {
      setTouched(true);
      notifyError('Please fill all the mandatory fields.');
      return;
    }
    const rangeProblem = dateRangeProblem(form.periodFrom, form.periodTo, 'The period');
    if (rangeProblem) {
      notifyError(rangeProblem);
      return;
    }
    if (!data?.clientId) {
      dialogRef.close(form);
      return;
    }
    setIsSaving(true);
    practiceService
      .savePeriod({
        clientId: data.clientId,
        id: data.period?.id ?? null,
        periodFrom: toIsoDate(form.periodFrom)!,
        periodTo: toIsoDate(form.periodTo)!,
        accountsDueDate: noDueDates ? null : toIsoDate(form.accountsDueDate)!,
        ct600DueDate: noDueDates ? null : toIsoDate(form.ct600DueDate)!,
        lockPreviousPeriod: !!form.lockPreviousPeriod,
        isCompanyCeased: !!form.isCompanyCeased,
        forComparative: !!form.forComparative,
      })
      .then(
        () => dialogRef.close(true),
        (error) => {
          setIsSaving(false);
          notifyError(error);
        },
      );
  };

  return (
    <div className="mat-dialog-wrapper new-account">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{data?.period ? 'Edit Accounting Period' : 'Add Accounting Period'}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="d-flex gap-30">
              <div className="form-field">
                <p>
                  Period From<span className="text-red">*</span>
                </p>
                <DateField
                  fieldClassName="w-180"
                  value={form.periodFrom}
                  invalid={touched && isBlank(form.periodFrom)}
                  onChange={onPeriodFromChange}
                />
              </div>
              <div className="form-field">
                <p>
                  To<span className="text-red">*</span>
                </p>
                <DateField
                  fieldClassName="w-180"
                  value={form.periodTo}
                  min={form.periodFrom && dayjs(form.periodFrom).isValid() ? dayjs(form.periodFrom) : undefined}
                  invalid={touched && isBlank(form.periodTo)}
                  onChange={onPeriodToChange}
                />
              </div>
            </div>
            {!noDueDates && (
              <div className="d-flex gap-30">
                <div className="form-field">
                  <p>
                    Accounts Due Date<span className="text-red">*</span>
                  </p>
                  <DateField
                    fieldClassName="w-180"
                    value={form.accountsDueDate}
                    invalid={touched && isBlank(form.accountsDueDate)}
                    onChange={(value: Dayjs | null) => set('accountsDueDate', value)}
                  />
                </div>
                <div className="form-field">
                  <p>
                    CT 600 Due Date<span className="text-red">*</span>
                  </p>
                  <DateField
                    fieldClassName="w-180"
                    value={form.ct600DueDate}
                    invalid={touched && isBlank(form.ct600DueDate)}
                    onChange={(value: Dayjs | null) => set('ct600DueDate', value)}
                  />
                </div>
              </div>
            )}
            <div className="d-flex flex-column">
              <Checkbox
                checked={form.lockPreviousPeriod}
                onChange={(event) => set('lockPreviousPeriod', event.target.checked)}
              >
                Lock Previous Period
              </Checkbox>
              <Checkbox
                checked={form.isCompanyCeased}
                onChange={(event) => set('isCompanyCeased', event.target.checked)}
              >
                Is Company Ceased?
              </Checkbox>
              <Checkbox checked={form.forComparative} onChange={(event) => set('forComparative', event.target.checked)}>
                For Comparative
              </Checkbox>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" type="button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
